import { Dispatch, SetStateAction, useEffect, useState } from 'react';

type SetValue<T> = Dispatch<SetStateAction<T>>;

type PersistedStateStorage = Pick<
  Storage,
  'getItem' | 'setItem' | 'removeItem'
>;

export function usePersistedState(
  key: string,
  initialValue: string | undefined,
  storage: PersistedStateStorage,
): [string | undefined, SetValue<string | undefined>] {
  const [state, setState] = useState<string | undefined>(() => {
    const storedValue = storage.getItem(key);
    return storedValue ? storedValue : initialValue;
  });

  useEffect(() => {
    if (state == null) {
      storage.removeItem(key);
    } else {
      storage.setItem(key, state);
    }
  }, [key, state, storage]);

  return [state, setState];
}

export function usePersistedJsonState<T>(
  key: string,
  initialValue: T | undefined,
  storage: PersistedStateStorage,
): [T, SetValue<T>] {
  const [state, setState] = useState<T>(() => {
    const storedValue = storage.getItem(key);
    return storedValue ? JSON.parse(storedValue) : initialValue;
  });

  useEffect(() => {
    if (state == null) {
      storage.removeItem(key);
    } else {
      const serializedState = JSON.stringify(state);

      if (storage.getItem(key) !== serializedState) {
        storage.setItem(key, serializedState);
      }
    }
  }, [key, state, storage]);

  return [state, setState];
}
